import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import Navbar from '../components/Navbar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Trophy, Search, Trash2, Star, Award, TrendingUp, Crown, Medal } from 'lucide-react';
import { toast } from 'sonner';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useOnlineIds } from '../components/OnlineUsers';

const RankingsPage = () => {
  const { API, user } = useContext(AuthContext);
  const onlineIds = useOnlineIds();
  const [rankings, setRankings] = useState([]);
  const [streaks, setStreaks] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('הכל');

  const categories = [
    { id: 'הכל', label: 'הכל' },
    { id: 'דרג א', label: 'דרג א' },
    { id: 'דרג ב', label: 'דרג ב' },
    { id: 'דרג ג', label: 'דרג ג' },
    { id: 'יחידים open', label: 'יחידים Open' },
    { id: 'זוגות open', label: 'זוגות Open' },
    { id: 'נשים', label: 'נשים' },
  ];

  useEffect(() => {
    fetchRankings();
  }, [selectedCategory]);

  useEffect(() => {
    axios.get(`${API}/streaks`)
      .then((res) => setStreaks(res.data.streaks || {}))
      .catch(() => setStreaks({}));
  }, [API]);

  const fetchRankings = async () => {
    try {
      const params = selectedCategory !== 'הכל' ? `?category=${selectedCategory}` : '';
      const response = await axios.get(`${API}/rankings${params}`);
      setRankings(response.data);
    } catch (error) {
      console.error('Failed to fetch rankings:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredRankings = searchTerm
    ? rankings.filter(player => 
        player.full_name?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : rankings;

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`האם למחוק את ${userName}?\n\nפעולה זו תמחק:\n- המשתמש\n- כל הרישומים לטורנירים\n\nהאם אתה בטוח?`)) {
      return;
    }
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${userName} נמחק בהצלחה!`);
      fetchRankings();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'שגיאה במחיקת משתמש');
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/).filter(Boolean);
    return parts.length > 1 ? `${parts[0][0]}${parts[1][0]}` : parts[0].substring(0, 2);
  };

  const TopThreePodium = () => {
    const top3 = filteredRankings.slice(0, 3);
    if (top3.length < 3) return null;

    const podiumConfig = [
      { player: top3[1], rank: 2, height: 'h-20 sm:h-28', avatarSize: 'h-16 w-16 sm:h-20 sm:w-20', bar: 'from-slate-300/90 to-slate-400/60', ring: 'from-slate-200 to-slate-400', glow: 'rgba(203,213,225,0.35)', label: 'סגן אלוף' },
      { player: top3[0], rank: 1, height: 'h-28 sm:h-40', avatarSize: 'h-20 w-20 sm:h-24 sm:w-24', bar: 'from-yellow-400/95 to-amber-500/70', ring: 'from-yellow-300 to-amber-500', glow: 'rgba(251,191,36,0.45)', label: 'אלוף' },
      { player: top3[2], rank: 3, height: 'h-16 sm:h-24', avatarSize: 'h-14 w-14 sm:h-16 sm:w-16', bar: 'from-amber-600/90 to-orange-800/60', ring: 'from-amber-500 to-orange-700', glow: 'rgba(217,119,6,0.35)', label: 'מקום 3' },
    ];

    return (
      <div
        className="relative rounded-3xl overflow-hidden mb-12 shadow-2xl brand-font"
        style={{ background: 'linear-gradient(160deg, #1e2b5e 0%, #17224d 55%, #0e1c3d 100%)' }}
        data-testid="podium-section"
      >
        {/* Brand glows */}
        <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full blur-3xl pointer-events-none" style={{ background: 'rgba(29,111,242,0.30)' }} />
        <div className="absolute -bottom-20 -left-16 w-64 h-64 rounded-full blur-3xl pointer-events-none" style={{ background: 'rgba(16,180,126,0.22)' }} />
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-80 h-40 rounded-full blur-3xl pointer-events-none" style={{ background: 'rgba(251,191,36,0.12)' }} />
        {/* Court line */}
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent" />

        <div className="relative px-4 pt-8 pb-0 sm:px-8">
          {/* Section title */}
          <div className="flex items-center justify-center gap-2 mb-8">
            <Crown className="w-5 h-5 text-yellow-400" />
            <h2 className="text-lg sm:text-xl font-bold text-white tracking-wide">מובילי הדירוג</h2>
            <Crown className="w-5 h-5 text-yellow-400" />
          </div>

          <div className="flex justify-center items-end gap-2 sm:gap-8">
            {podiumConfig.map(({ player, rank, height, avatarSize, bar, ring, glow, label }) => (
              <Link
                key={player.id}
                to={`/profile/${player.id}`}
                className="group flex flex-col items-center flex-1 max-w-[130px] sm:max-w-[160px]"
                data-testid={`podium-player-${rank}`}
              >
                {/* Crown for champion */}
                {rank === 1 && (
                  <Crown className="w-7 h-7 sm:w-8 sm:h-8 text-yellow-400 mb-1 drop-shadow-[0_0_8px_rgba(251,191,36,0.8)] animate-pulse" />
                )}

                {/* Avatar with glowing gradient ring */}
                <div className="relative mb-3 transition-transform duration-300 group-hover:scale-105">
                  <div
                    className={`rounded-full p-[3px] bg-gradient-to-br ${ring}`}
                    style={{ boxShadow: `0 0 24px ${glow}` }}
                  >
                    <Avatar className={`${avatarSize} border-2 border-[#17224d]`}>
                      <AvatarImage src={player.avatar} alt={player.full_name} />
                      <AvatarFallback className={`bg-gradient-to-br ${ring} text-white text-lg font-bold`}>
                        {getInitials(player.full_name)}
                      </AvatarFallback>
                    </Avatar>
                  </div>
                  <div className={`absolute -bottom-2 left-1/2 -translate-x-1/2 w-7 h-7 rounded-full bg-gradient-to-br ${ring} flex items-center justify-center text-[#1e2b5e] font-black text-sm shadow-lg border-2 border-[#17224d]`}>
                    {rank}
                  </div>
                  {onlineIds.has(player.id) && (
                    <span className="absolute top-0 left-0 w-4 h-4 bg-[#10b47e] rounded-full border-2 border-[#17224d]" title="מחובר עכשיו"></span>
                  )}
                </div>

                {/* Name */}
                <p className="text-center font-bold text-white text-xs sm:text-base w-full truncate mt-1 group-hover:text-cyan-300 transition-colors">
                  {player.full_name}
                </p>
                {/* Points pill */}
                <span className="mt-1.5 mb-3 inline-flex items-center gap-1 bg-white/10 border border-white/15 backdrop-blur-sm text-white/90 text-[11px] sm:text-xs font-bold px-2.5 py-1 rounded-full">
                  <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                  {player.total_points || 0} נק׳
                </span>

                {/* Podium bar - glassy */}
                <div className={`${height} w-full bg-gradient-to-t ${bar} rounded-t-2xl relative overflow-hidden border-t border-x border-white/20 backdrop-blur-sm flex flex-col items-center pt-2 sm:pt-3`}>
                  <span className="text-white font-black text-2xl sm:text-4xl drop-shadow-md leading-none">{rank}</span>
                  <span className="text-white/85 text-[10px] sm:text-xs font-bold mt-0.5">{label}</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent pointer-events-none" />
                </div>
              </Link>
            ))}
          </div>
        </div>
        {/* Podium floor */}
        <div className="relative h-2.5 bg-gradient-to-r from-[#1d6ff2]/60 via-yellow-400/70 to-[#10b47e]/60" />
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50/30">
      <Navbar />

      <div className="max-w-6xl mx-auto px-4 py-10">
        {/* Header */}
        <div className="text-center mb-10 brand-font">
          <div className="inline-flex items-center gap-2 bg-[#1d6ff2]/10 text-[#1d6ff2] border border-[#1d6ff2]/20 rounded-full px-4 py-1.5 text-sm font-bold mb-4">
            <Medal className="w-4 h-4" />
            טבלת האלופים
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-[#1e2b5e] dark:text-white mb-3" data-testid="rankings-title">
            דירוג שחקנים
          </h1>
          <p className="text-base text-slate-500">
            הטובים ביותר בטניס הישראלי 🎾
          </p>
        </div>

        {/* Search */}
        <div className="mb-8 max-w-md mx-auto">
          <div className="relative">
            <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              type="text"
              placeholder="חפש שחקן..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pr-12 py-5 text-base rounded-xl border-2 border-gray-200 focus:border-teal-500 shadow-sm"
              data-testid="search-input"
            />
          </div>
        </div>

        {/* Category Tabs - Clean, no icons */}
        <div className="mb-10" data-testid="category-tabs">
          <div className="flex flex-wrap justify-center gap-2">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                data-testid={`category-tab-${category.id}`}
                className={`
                  px-5 py-2.5 rounded-full font-semibold text-sm transition-all duration-200
                  ${selectedCategory === category.id
                    ? 'bg-[#1e2b5e] text-white shadow-md scale-[1.03]'
                    : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                  }
                `}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="py-16">
            <LoadingSpinner size="lg" text="טוען דירוג שחקנים..." />
          </div>
        ) : (
          <>
            {/* Top 3 Podium */}
            <TopThreePodium />

            {/* Rankings Table */}
            <Card className="overflow-hidden shadow-xl border-0 bg-white" data-testid="rankings-table-card">
              <CardHeader className="text-white py-5 px-6" style={{ background: 'linear-gradient(135deg, #1e2b5e 0%, #17224d 60%, #10203f 100%)' }}>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2 font-bold">
                    <Award className="w-5 h-5" />
                    {selectedCategory === 'הכל' ? 'דירוג כללי' : `דירוג ${selectedCategory}`}
                  </CardTitle>
                  <span className="text-xs bg-white/20 px-3 py-1 rounded-full">
                    {filteredRankings.length} שחקנים
                  </span>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {filteredRankings.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200">
                          <th className="text-right py-3 px-5 font-semibold text-gray-500 text-xs uppercase tracking-wider">#</th>
                          <th className="text-right py-3 px-5 font-semibold text-gray-500 text-xs uppercase tracking-wider">שחקן</th>
                          <th className="text-right py-3 px-5 font-semibold text-gray-500 text-xs uppercase tracking-wider">רמה</th>
                          <th className="text-center py-3 px-5 font-semibold text-gray-500 text-xs uppercase tracking-wider">ניקוד</th>
                          <th className="text-center py-3 px-5 font-semibold text-gray-500 text-xs uppercase tracking-wider">ניצחונות</th>
                          <th className="text-center py-3 px-5 font-semibold text-gray-500 text-xs uppercase tracking-wider">תחרויות</th>
                          {['shafirroy@icloud.com', 'shafirroy@gmail.com'].includes(user?.email) && (
                            <th className="text-center py-3 px-5 font-semibold text-gray-500 text-xs uppercase tracking-wider">פעולות</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredRankings.map((player, index) => (
                          <tr 
                            key={player.id} 
                            className="border-b border-gray-50 hover:bg-teal-50/40 transition-colors"
                            data-testid={`ranking-row-${player.id}`}
                          >
                            {/* Rank */}
                            <td className="py-3.5 px-5">
                              {index < 3 ? (
                                <div className={`
                                  w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm
                                  ${index === 0 ? 'bg-yellow-400 text-yellow-900' : ''}
                                  ${index === 1 ? 'bg-slate-300 text-slate-700' : ''}
                                  ${index === 2 ? 'bg-amber-600 text-white' : ''}
                                `}>
                                  {player.rank}
                                </div>
                              ) : (
                                <span className="text-gray-400 font-medium pr-2">{player.rank}</span>
                              )}
                            </td>
                            
                            {/* Player Info */}
                            <td className="py-3.5 px-5">
                              <Link 
                                to={`/profile/${player.id}`} 
                                className="flex items-center gap-3 group"
                                data-testid={`player-link-${player.id}`}
                              >
                                <div className="relative shrink-0">
                                  <Avatar className="h-10 w-10 ring-1 ring-gray-100 shadow-sm">
                                    <AvatarImage src={player.avatar} alt={player.full_name} />
                                    <AvatarFallback className="bg-gradient-to-br from-teal-500 to-cyan-500 text-white font-bold text-sm">
                                      {getInitials(player.full_name)}
                                    </AvatarFallback>
                                  </Avatar>
                                  {onlineIds.has(player.id) && (
                                    <span
                                      className="absolute bottom-0 left-0 w-3 h-3 bg-[#10b47e] rounded-full border-2 border-white dark:border-slate-900"
                                      title="מחובר עכשיו"
                                      data-testid={`online-dot-${player.id}`}
                                    ></span>
                                  )}
                                </div>
                                <span className="font-semibold text-gray-800 group-hover:text-teal-600 transition-colors">
                                  {player.full_name}
                                </span>
                                {streaks[player.id] && (
                                  <span
                                    className="flex items-center gap-0.5 bg-orange-100 text-orange-700 text-xs font-extrabold px-2 py-0.5 rounded-full"
                                    title={`${streaks[player.id]} ניצחונות ברצף`}
                                    data-testid={`streak-badge-${player.id}`}
                                  >
                                    🔥{streaks[player.id]}
                                  </span>
                                )}
                              </Link>
                            </td>
                            
                            {/* Skill Level */}
                            <td className="py-3.5 px-5">
                              <span className="inline-block whitespace-nowrap px-2.5 py-1 bg-[#1d6ff2]/10 text-[#1d6ff2] border border-[#1d6ff2]/15 rounded-full text-xs font-bold">
                                {player.skill_level || 'לא צוין'}
                              </span>
                            </td>
                            
                            {/* Points */}
                            <td className="py-3.5 px-5 text-center">
                              <span className="font-bold text-gray-900">{player.total_points || 0}</span>
                            </td>
                            
                            {/* Wins */}
                            <td className="py-3.5 px-5 text-center">
                              <span className="font-semibold text-teal-600">{player.total_wins || 0}</span>
                            </td>
                            
                            {/* Tournaments */}
                            <td className="py-3.5 px-5 text-center">
                              <span className="text-gray-600">{player.total_tournaments || 0}</span>
                            </td>
                            
                            {/* Admin Actions */}
                            {['shafirroy@icloud.com', 'shafirroy@gmail.com'].includes(user?.email) && (
                              <td className="py-3.5 px-5 text-center">
                                {!player.is_admin && player.id !== user?.id && (
                                  <Button
                                    variant="destructive"
                                    size="sm"
                                    onClick={() => handleDeleteUser(player.id, player.full_name)}
                                    className="flex items-center gap-1 mx-auto"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                    מחק
                                  </Button>
                                )}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <Trophy className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-lg text-gray-500">אין שחקנים בדירוג</p>
                    <p className="text-gray-400 mt-1 text-sm">נסה לחפש קטגוריה אחרת</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default RankingsPage;
